import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import { ArrowRight, Coins, Gauge, ImagePlus, MousePointerClick, RefreshCw, Sparkles, UserPlus, Users } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { toast } from 'sonner';

import { PeriodControl } from '../../../components/design-system/analytics/PeriodControl';
import { PageContainer } from '../../../common/components/PageContainer';
import { AdSuggestion, AdsOverview, useAdsApi } from '../../../common/hooks/use-ads-api.hook';
import { FeatureGate } from '../../../components/FeatureGate';
import { StatCard } from '../../../modules/vendor/dashboard/components/stat-card.component';
import {
    BarList, CampaignStatusBadge, ConnectFirst, PageHeading, ProviderMark, changeOf, formatMoney, formatNumber,
    formatPct, formatRoas, readApiError, useAdConnections, useAdsRange,
} from './ads-shared';

type Metric = 'spend' | 'leads' | 'clicks';

const OverviewBody = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const api = useAdsApi();
    const connections = useAdConnections();
    const { range, periodProps } = useAdsRange();
    const [data, setData] = useState<AdsOverview | null>(null);
    const [suggestions, setSuggestions] = useState<AdSuggestion[]>([]);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [metric, setMetric] = useState<Metric>('spend');
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        if (!connections.hasConnection) return;
        let cancelled = false;
        setLoading(true);
        Promise.all([api.getOverview(range), api.getSuggestions('open').catch(() => [])])
            .then(([overview, open]) => {
                if (cancelled) return;
                setData(overview);
                setSuggestions(open);
            })
            .catch((error) => {
                console.error('Error loading ads overview:', error);
                if (!cancelled) setData(null);
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [range.startDate, range.endDate, connections.hasConnection, reloadKey]);

    const syncNow = async () => {
        setSyncing(true);
        try {
            const result = await api.sync();
            const failed = (result.results ?? []).filter((r) => !r.ok);
            if (failed.length) {
                toast.error(t('ADS.SYNC.PARTIAL', '{{count}} account(s) could not be read: {{reason}}', {
                    count: failed.length, reason: failed[0].error,
                }));
            } else {
                toast.success(t('ADS.SYNC.DONE', 'Your ad accounts are up to date.'));
            }
            await api.refreshSuggestions().catch(() => undefined);
            setReloadKey((k) => k + 1);
            connections.reload();
        } catch (error) {
            toast.error(await readApiError(error, t('ADS.SYNC.FAILED', 'The accounts could not be synced.')));
        } finally {
            setSyncing(false);
        }
    };

    const heading = (
        <PageHeading
            title={t('ADS.OVERVIEW.TITLE', 'Ads')}
            subtitle={t(
                'ADS.OVERVIEW.SUBTITLE',
                'What your Meta and Google spending brought to your site — not just clicks, but people who tried your products in their rooms and left their details.',
            )}
        >
            {connections.hasConnection && (
                <>
                    <Button variant="outline" size="sm" onClick={syncNow} disabled={syncing}>
                        {syncing ? <Spinner data-icon="inline-start" /> : <RefreshCw data-icon="inline-start" />}
                        {t('ADS.SYNC.BUTTON', 'Sync now')}
                    </Button>
                    <PeriodControl {...periodProps} />
                </>
            )}
        </PageHeading>
    );

    if (connections.loading) {
        return <div className="flex flex-col gap-6">{heading}<Skeleton className="h-64 w-full" /></div>;
    }
    if (!connections.hasConnection) {
        return <div className="flex flex-col gap-6">{heading}<ConnectFirst /></div>;
    }

    const totals = data?.totals;
    const currency = totals?.currency ?? null;
    const reauth = connections.data?.connections.filter((c) => c.status === 'needs_reauth') ?? [];

    const metricLabels: Record<Metric, string> = {
        spend: t('ADS.METRIC.SPEND', 'Spend'),
        leads: t('ADS.METRIC.LEADS', 'Enquiries'),
        clicks: t('ADS.METRIC.CLICKS', 'Clicks'),
    };
    // One series at a time: money and head-counts do not share a scale.
    const chartConfig: ChartConfig = { [metric]: { label: metricLabels[metric], color: 'var(--foreground)' } };

    const stat = (title: string, value: string | number, icon: React.ReactNode, hint = '', change: number | null = null) => (
        <StatCard title={title} value={value} icon={icon} loading={loading} percentageChange={change ?? 0} comparisonLabel={hint} />
    );

    const pacing = data?.pacing;
    const pacePct = pacing?.budget ? Math.min(100, Math.round((pacing.spend / pacing.budget) * 100)) : 0;

    return (
        <div className="flex flex-col gap-6">
            {heading}

            {reauth.length > 0 && (
                <Alert variant="destructive">
                    <AlertTitle>{t('ADS.OVERVIEW.REAUTH_TITLE', 'An ad account needs reconnecting')}</AlertTitle>
                    <AlertDescription className="flex flex-wrap items-center gap-3">
                        {t('ADS.OVERVIEW.REAUTH_BODY', '{{names}} stopped answering. The figures below are from its last sync.', {
                            names: reauth.map((c) => c.accountName).join(', '),
                        })}
                        <Button variant="outline" size="sm" onClick={() => navigate('/ads/connections')}>
                            {t('ADS.OVERVIEW.REAUTH_CTA', 'Reconnect')}
                        </Button>
                    </AlertDescription>
                </Alert>
            )}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {stat(t('ADS.METRIC.SPEND', 'Spend'), formatMoney(totals?.spend, currency), <Coins />,
                    totals ? t('ADS.OVERVIEW.SPEND_HINT', '{{count}} active campaigns', { count: totals.activeCampaigns }) : '')}
                {/* The only tile whose rise is good news, so the only one given the arrow. */}
                {stat(t('ADS.METRIC.LEADS', 'Enquiries'), formatNumber(totals?.leads), <UserPlus />,
                    t('ADS.OVERVIEW.LEADS_HINT', 'People from your ads who left their details'),
                    changeOf(totals?.leads, data?.previous?.leads))}
                {stat(t('ADS.METRIC.COST_PER_LEAD', 'Cost per enquiry'), formatMoney(totals?.costPerLead, currency, 2), <Gauge />,
                    data?.previous?.costPerLead
                        ? t('ADS.OVERVIEW.PREVIOUS', 'Previous period: {{value}}', { value: formatMoney(data.previous.costPerLead, currency, 2) })
                        : '')}
                {stat(t('ADS.METRIC.ROAS', 'Return claimed by platforms'), formatRoas(totals?.providerRoas), <Sparkles />,
                    t('ADS.OVERVIEW.ROAS_HINT', 'As Meta and Google report it'))}
                {stat(t('ADS.METRIC.ENGAGED', 'Engaged visitors'), formatNumber(totals?.engagements), <Users />,
                    totals ? t('ADS.OVERVIEW.ENGAGED_HINT', '{{cost}} each', { cost: formatMoney(totals.costPerEngagement, currency, 2) }) : '')}
                {stat(t('ADS.METRIC.ROOM_UPLOADS', 'Rooms uploaded'), formatNumber(totals?.roomUploads), <ImagePlus />,
                    t('ADS.OVERVIEW.ROOMS_HINT', 'Visitors who photographed their own room'))}
                {stat(t('ADS.METRIC.CLICKS', 'Clicks'), formatNumber(totals?.clicks), <MousePointerClick />,
                    totals ? t('ADS.OVERVIEW.CTR_HINT', '{{ctr}} of impressions', { ctr: formatPct(totals.ctr) }) : '')}
                {stat(t('ADS.METRIC.CHATS', 'Assistant chats'), formatNumber(totals?.chats), <Sparkles />,
                    t('ADS.OVERVIEW.CHATS_HINT', 'Conversations started from an ad'))}
            </div>

            <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
                <Card className="xl:col-span-2">
                    <CardHeader>
                        <CardTitle>{metricLabels[metric]}</CardTitle>
                        <CardAction>
                            <ToggleGroup type="single" variant="outline" size="sm" value={metric} onValueChange={(v) => v && setMetric(v as Metric)}>
                                {(Object.keys(metricLabels) as Metric[]).map((key) => (
                                    <ToggleGroupItem key={key} value={key}>{metricLabels[key]}</ToggleGroupItem>
                                ))}
                            </ToggleGroup>
                        </CardAction>
                    </CardHeader>
                    <CardContent>
                        {loading ? <Skeleton className="h-72 w-full" /> : (
                            <ChartContainer config={chartConfig} className="h-72 w-full">
                                <AreaChart data={data?.series || []} margin={{ left: 0, right: 8, top: 8 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="date" tickLine={false} axisLine={false} minTickGap={24} tickFormatter={(d: string) => d.slice(5)} />
                                    <YAxis allowDecimals={metric === 'spend'} tickLine={false} axisLine={false} width={44} />
                                    <ChartTooltip content={<ChartTooltipContent />} />
                                    <Area dataKey={metric} type="monotone" stroke={`var(--color-${metric})`} strokeWidth={2}
                                        fill={`var(--color-${metric})`} fillOpacity={0.08} />
                                </AreaChart>
                            </ChartContainer>
                        )}
                    </CardContent>
                </Card>

                <div className="flex flex-col gap-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>{t('ADS.PACING.TITLE', 'This month')}</CardTitle>
                            <CardDescription>
                                {pacing?.budget
                                    ? t('ADS.PACING.OF_BUDGET', '{{spend}} of {{budget}}', {
                                        spend: formatMoney(pacing.spend, currency), budget: formatMoney(pacing.budget, currency),
                                    })
                                    : t('ADS.PACING.NO_BUDGET', 'No monthly budget set.')}
                            </CardDescription>
                            <CardAction>
                                <Button variant="ghost" size="sm" onClick={() => navigate('/ads/budgets')}>
                                    {pacing?.budget ? t('ADS.PACING.EDIT', 'Edit') : t('ADS.PACING.SET', 'Set budget')}
                                </Button>
                            </CardAction>
                        </CardHeader>
                        <CardContent className="flex flex-col gap-3">
                            {loading ? <Skeleton className="h-10 w-full" /> : pacing?.budget ? (
                                <>
                                    <Progress value={pacePct} />
                                    <p className="text-sm text-muted-foreground">
                                        {pacing.pace === null ? '' : pacing.pace > 1.15
                                            ? t('ADS.PACING.AHEAD', 'Ahead of plan — on course for {{projected}}.', { projected: formatMoney(pacing.projected, currency) })
                                            : pacing.pace < 0.7
                                                ? t('ADS.PACING.BEHIND', 'Behind plan — on course for {{projected}}.', { projected: formatMoney(pacing.projected, currency) })
                                                : t('ADS.PACING.ON_TRACK', 'On track — on course for {{projected}}.', { projected: formatMoney(pacing.projected, currency) })}
                                    </p>
                                </>
                            ) : (
                                <p className="text-sm text-muted-foreground">
                                    {t('ADS.PACING.EXPLAIN', 'Set what you mean to spend each month and we will tell you when spending runs ahead or behind.')}
                                </p>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader><CardTitle>{t('ADS.OVERVIEW.BY_PLATFORM', 'By platform')}</CardTitle></CardHeader>
                        <CardContent>
                            {loading ? <Skeleton className="h-16 w-full" /> : (
                                <BarList
                                    empty={t('ADS.COMMON.NO_DATA', 'Nothing in this period yet.')}
                                    items={(data?.byProvider ?? []).map((p) => ({
                                        label: p.provider === 'google' ? 'Google' : 'Meta',
                                        value: Math.round(p.spend),
                                        hint: t('ADS.OVERVIEW.PLATFORM_HINT', '· {{leads}} enquiries', { leads: p.leads }),
                                    }))}
                                />
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
                <Card className="xl:col-span-2">
                    <CardHeader>
                        <CardTitle>{t('ADS.OVERVIEW.CAMPAIGNS', 'Campaigns')}</CardTitle>
                        <CardDescription>{t('ADS.OVERVIEW.CAMPAIGNS_DESC', 'Ranked by spend. Enquiries are counted on your site, not by the platform.')}</CardDescription>
                        <CardAction>
                            <Button variant="ghost" size="sm" onClick={() => navigate('/ads/campaigns')}>
                                {t('ADS.COMMON.SEE_ALL', 'See all')}
                                <ArrowRight data-icon="inline-end" />
                            </Button>
                        </CardAction>
                    </CardHeader>
                    <CardContent>
                        {loading ? <Skeleton className="h-48 w-full" /> : (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>{t('ADS.CAMPAIGNS.NAME', 'Campaign')}</TableHead>
                                        <TableHead>{t('ADS.CAMPAIGNS.STATUS', 'Status')}</TableHead>
                                        <TableHead className="text-end">{t('ADS.METRIC.SPEND', 'Spend')}</TableHead>
                                        <TableHead className="text-end">{t('ADS.METRIC.LEADS', 'Enquiries')}</TableHead>
                                        <TableHead className="text-end">{t('ADS.METRIC.COST_PER_LEAD', 'Cost per enquiry')}</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {(data?.topCampaigns ?? []).slice(0, 6).map((c) => (
                                        <TableRow key={`${c.provider}:${c.externalId}`}>
                                            <TableCell>
                                                <span className="flex min-w-0 items-center gap-2">
                                                    <ProviderMark provider={c.provider} />
                                                    <span className="truncate">{c.name}</span>
                                                </span>
                                            </TableCell>
                                            <TableCell><CampaignStatusBadge status={c.status} /></TableCell>
                                            <TableCell className="text-end tabular-nums">{formatMoney(c.spend, c.currency ?? currency)}</TableCell>
                                            <TableCell className="text-end tabular-nums">{c.ours.leads}</TableCell>
                                            <TableCell className="text-end tabular-nums">{formatMoney(c.costPerLead, c.currency ?? currency, 2)}</TableCell>
                                        </TableRow>
                                    ))}
                                    {!data?.topCampaigns?.length && (
                                        <TableRow>
                                            <TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">
                                                {t('ADS.CAMPAIGNS.EMPTY', 'No campaigns yet. Press Sync now to pull them from your accounts.')}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        )}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2"><Sparkles className="size-4" />{t('ADS.SUGGESTIONS.TITLE', 'Suggestions')}</CardTitle>
                        <CardDescription>{t('ADS.SUGGESTIONS.SHORT_DESC', 'Worked out from your numbers.')}</CardDescription>
                        <CardAction>
                            <Button variant="ghost" size="sm" onClick={() => navigate('/ads/suggestions')}>
                                {t('ADS.COMMON.SEE_ALL', 'See all')}
                                <ArrowRight data-icon="inline-end" />
                            </Button>
                        </CardAction>
                    </CardHeader>
                    <CardContent>
                        {loading ? <Skeleton className="h-40 w-full" /> : suggestions.length ? (
                            <ul className="flex flex-col divide-y">
                                {suggestions.slice(0, 4).map((s) => (
                                    <li key={s.uuid}>
                                        <button
                                            type="button"
                                            onClick={() => navigate(`/ads/suggestions?focus=${s.uuid}`)}
                                            className="flex w-full flex-col gap-1 py-3 text-start transition-colors hover:bg-accent/40"
                                        >
                                            <span className="text-sm font-medium">{s.title}</span>
                                            <span className="line-clamp-2 text-xs text-muted-foreground">{s.detail}</span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="py-6 text-center text-sm text-muted-foreground">
                                {t('ADS.SUGGESTIONS.NONE_SHORT', 'Nothing to change right now.')}
                            </p>
                        )}
                    </CardContent>
                </Card>
            </div>

            {!!data?.unmatchedCampaigns?.length && (
                <Alert>
                    <AlertTitle>{t('ADS.OVERVIEW.UNMATCHED_TITLE', 'Traffic from campaigns we cannot see')}</AlertTitle>
                    <AlertDescription>
                        {t(
                            'ADS.OVERVIEW.UNMATCHED_BODY',
                            'Visitors arrived tagged with {{campaigns}}, which match no connected campaign. Often an account run by an agency that is not connected yet.',
                            { campaigns: data.unmatchedCampaigns.slice(0, 3).map((u) => `“${u.campaign}”`).join(', ') },
                        )}
                    </AlertDescription>
                </Alert>
            )}
        </div>
    );
};

export const AdsOverviewPage = () => {
    const { t } = useTranslation();
    return (
        <PageContainer breadcrumbs={[{ label: t('SIDEBAR.ADS.TITLE', 'Ads') }, { label: t('SIDEBAR.DASHBOARD.TITLE') }]}>
            <FeatureGate feature="adsManager">
                <OverviewBody />
            </FeatureGate>
        </PageContainer>
    );
};

export default AdsOverviewPage;
